import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { useCart } from "../cart";
import { FGM } from "../brand";
import { track } from "../analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Success() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState({ payment_status: "pending", status: "open" });
    const [attempts, setAttempts] = useState(0);
    const [error, setError] = useState(null);
    const { clear } = useCart();

    useEffect(() => {
        if (!sessionId) {
            setError("Missing session id");
            return;
        }
        let cancelled = false;
        let cleared = false;

        const poll = async (count) => {
            if (cancelled) return;
            // 30 attempts × 2s = 60s — gives Stripe webhook plenty of time even under load
            if (count >= 30) {
                setError(
                    "Still confirming with Stripe. Your card was charged successfully — " +
                    "check your email for the receipt and your order will appear in our system shortly."
                );
                return;
            }
            try {
                const res = await axios.get(`${API}/checkout/status/${sessionId}`);
                if (cancelled) return;
                setStatus(res.data);
                setAttempts(count + 1);
                if (res.data.payment_status === "paid") {
                    if (!cleared) {
                        track("purchase", {
                            transaction_id: sessionId,
                            currency: (res.data.currency || "usd").toUpperCase(),
                            value: (res.data.amount_total || 0) / 100,
                        });
                        clear();
                        cleared = true;
                    }
                    return;
                }
                if (res.data.status === "expired") {
                    setError("Payment session expired. Please try again.");
                    return;
                }
                setTimeout(() => poll(count + 1), 2000);
            } catch (_e) {
                setError("Couldn't check payment status. Try again.");
            }
        };

        poll(0);
        return () => {
            cancelled = true;
        };
    }, [sessionId, clear]);

    const isPaid = status.payment_status === "paid";

    return (
        <div data-testid="success-page" className="min-h-screen bg-fgm-dark grain flex items-center justify-center px-6 py-20">
            <div className="max-w-xl w-full bg-fgm-surface border border-white/10 p-8 md:p-12 text-center">
                <img src={FGM.logos.white} alt="FGM" className="h-16 w-16 mx-auto mb-6"  loading="lazy" decoding="async" />
                <div className="fgm-stripe-divider w-20 mx-auto mb-6" />

                {error ? (
                    <>
                        <h1 className="font-display text-4xl text-white uppercase mb-3">
                            Something Went Off
                        </h1>
                        <p className="text-zinc-400 mb-8">{error}</p>
                    </>
                ) : isPaid ? (
                    <>
                        <div className="font-display text-6xl text-[#E63946] mb-4">✓</div>
                        <h1 data-testid="success-title" className="font-display text-4xl md:text-5xl text-white uppercase mb-3">
                            You're In The <br />Movement
                        </h1>
                        <p className="text-zinc-400 mb-2">
                            Payment received. Order confirmed.
                        </p>
                        <p className="text-zinc-500 text-sm mb-8">
                            ${(status.amount_total / 100).toFixed(2)} {String(status.currency).toUpperCase()}
                        </p>
                    </>
                ) : (
                    <>
                        <h1 className="font-display text-4xl text-white uppercase mb-3">
                            Confirming…
                        </h1>
                        <p className="text-zinc-400 mb-8">
                            Locking in your order. Don't close this tab.
                        </p>
                        <div className="text-xs font-display tracking-[0.3em] text-zinc-500 uppercase">
                            Checking · attempt {attempts}/30
                        </div>
                    </>
                )}

                <Link
                    to="/"
                    data-testid="success-back-home"
                    className="btn-primary mt-4 inline-flex"
                >
                    Back To Home
                </Link>
            </div>
        </div>
    );
}
